import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';

import { usePageTitle } from '../hooks/usePageTitle';
import {
  ContentOverride,
  fetchContent,
  fetchRoster,
  normalizePhone,
  sanitizeKourtContent,
  sanitizeRosterContent,
  sanitizeTeamContent,
  type Guideline,
  type KourtContent,
  type Line,
  type Match,
  type MatchResult,
  type Notice,
  type Player,
  type RosterContent,
  type RosterPlayer,
  type TeamContent,
  type WeatherHour,
} from '../team/content';
import { emptyScores, lineResult, sanitizeScores, upcomingMatch, type ScoreData, type SetScore } from '../team/match';
import { loadRuntimeSettings } from '../team/storage';
import Kourt from './Kourt';
import Team from './Team';
import './Admin.css';

/** The admin passcode, kept for the tab only — never localStorage. */
const ADMIN_KEY = 'tlh-admin-passcode';

type Kind = 'team' | 'kourt' | 'roster';
const KIND_LABEL: Record<Kind, string> = { team: 'Team page', kourt: 'Kourt page', roster: 'Roster' };

interface Version {
  name: string;
  at: string;
  by: string;
  summary: string;
}

/* ── Small form helpers ────────────────────────────────────────────────── */

function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="admin-field">
      <span className="admin-label">{label}</span>
      {children}
      {hint && <small className="admin-hint">{hint}</small>}
    </label>
  );
}

function Text({
  value,
  onChange,
  rows,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
}) {
  return rows ? (
    <textarea value={value} rows={rows} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
  ) : (
    <input type="text" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
  );
}

function Num({ value, onChange, min, max }: { value: number; onChange: (v: number) => void; min?: number; max?: number }) {
  return (
    <input
      type="number"
      value={Number.isFinite(value) ? value : ''}
      min={min}
      max={max}
      onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
    />
  );
}

function Section({ title, children, open }: { title: string; children: ReactNode; open?: boolean }) {
  return (
    <details className="admin-section" open={open}>
      <summary>{title}</summary>
      <div className="admin-section-body">{children}</div>
    </details>
  );
}

const lines = (s: string) =>
  s
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

/* ── The live scoreboard (shared state, not part of the content draft) ─── */

async function readLiveScores(content: TeamContent): Promise<ScoreData | null> {
  const { scores } = await loadRuntimeSettings();
  if (!scores) return null;
  try {
    const response = await fetch(`${scores.blobUrl}?${scores.sas}`, { cache: 'no-store' });
    if (!response.ok) return emptyScores(content);
    return sanitizeScores(await response.json(), content);
  } catch {
    return null;
  }
}

/** PUT the board and tell every open page over the relay; throws on a storage failure. */
async function writeLiveScores(payload: ScoreData): Promise<void> {
  const { scores } = await loadRuntimeSettings();
  if (!scores) throw new Error('Scoreboard storage is not configured.');
  const response = await fetch(`${scores.blobUrl}?${scores.sas}`, {
    method: 'PUT',
    headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) throw new Error(`Could not write the scoreboard (HTTP ${response.status}).`);
  try {
    const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const socket = new WebSocket(`${scheme}://${window.location.host}/ws/scores`);
    socket.onopen = () => {
      socket.send(JSON.stringify({ type: 'scores', data: payload }));
      socket.close();
    };
  } catch {
    /* the 30 s poll will carry it */
  }
}

/** The set inputs, exactly as the Team page has them, posting straight to the live blob. */
function LiveScoreboard({ draft, setStatus }: { draft: TeamContent; setStatus: (s: string) => void }) {
  const [scores, setScores] = useState<ScoreData | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const shape = `${draft.seasonKey}|${draft.lineup.length}|${draft.setsPerLine}`;

  const load = useCallback(async () => {
    setScores(await readLiveScores(draft));
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shape]);

  useEffect(() => {
    void load();
  }, [load]);

  const update = (line: number, set: number, side: keyof SetScore, value: string) => {
    const parsed = value === '' ? null : Math.max(0, Math.min(99, Number(value)));
    if (parsed !== null && Number.isNaN(parsed)) return;
    setScores((current) =>
      current
        ? { ...current, lines: current.lines.map((l, i) => (i === line ? l.map((x, j) => (j === set ? { ...x, [side]: parsed } : x)) : l)) }
        : current,
    );
    setDirty(true);
  };

  const post = async () => {
    if (!scores) return;
    setSaving(true);
    try {
      const payload: ScoreData = { ...scores, season: draft.seasonKey, updatedAt: new Date().toISOString() };
      await writeLiveScores(payload);
      setScores(payload);
      setDirty(false);
      setStatus(`Scores posted to the live board at ${new Date().toLocaleTimeString()} — every open page and the app show them now.`);
    } catch (e) {
      setStatus(e instanceof Error ? e.message : 'Could not post the scores.');
    }
    setSaving(false);
  };

  if (!scores) return <p className="admin-hint">Live scoreboard: not configured or unreachable.</p>;
  const tally = scores.lines.map(lineResult);
  const us = tally.filter((t) => t.winner === 'us').length;
  const them = tally.filter((t) => t.winner === 'them').length;
  return (
    <div className="admin-live">
      <div className="admin-live-head">
        <strong>Live scoreboard</strong>
        <span className="admin-hint">
          Shared state, separate from the draft: <strong>Post scores</strong> writes it for everyone immediately (no email goes
          out from here). Season key {scores.season}.{' '}
          {scores.updatedAt ? `Last posted ${new Date(scores.updatedAt).toLocaleString()}.` : 'Nothing posted yet this week.'}
        </span>
      </div>
      <div className="admin-grid admin-grid-scores">
        {draft.lineup.map((line, i) => {
          const r = tally[i] ?? { us: 0, them: 0, winner: null };
          return (
            <div key={line.id} className={`admin-row admin-score-row${r.winner ? ` admin-score-${r.winner}` : ''}`}>
              <span className="admin-score-line">
                <strong>{line.id}</strong>
                <small>{line.players ? line.players.map((p) => p.name).join(' / ') : 'Lineup not posted'}</small>
              </span>
              {(scores.lines[i] ?? []).map((set, j) => (
                <span key={j} className="admin-score-set">
                  <input type="number" min={0} max={99} inputMode="numeric" placeholder="–" aria-label={`${line.id} set ${j + 1} us`} value={set.us ?? ''} onChange={(e) => update(i, j, 'us', e.target.value)} />
                  <span>:</span>
                  <input type="number" min={0} max={99} inputMode="numeric" placeholder="–" aria-label={`${line.id} set ${j + 1} them`} value={set.them ?? ''} onChange={(e) => update(i, j, 'them', e.target.value)} />
                </span>
              ))}
              <span className={`admin-score-result admin-score-result-${r.winner ?? 'none'}`}>
                {r.winner === 'us' ? 'TLH ✔' : r.winner === 'them' ? 'Lost' : r.us + r.them > 0 ? `${r.us}–${r.them} sets` : '—'}
              </span>
            </div>
          );
        })}
      </div>
      <div className="admin-actions">
        <button type="button" className="admin-primary" onClick={post} disabled={saving || !dirty}>
          {saving ? 'Posting…' : dirty ? '🎾 Post scores (live)' : 'Scores posted'}
        </button>
        <button type="button" onClick={load} disabled={saving}>
          Refresh from the board
        </button>
        <span className="admin-hint">Lines: TLH {us} – {them}</span>
      </div>
    </div>
  );
}

/* ── Forecast (Open-Meteo, fetched by the browser) ─────────────────────── */

function firstServeHour(lineup: Line[]): number {
  const t = lineup.map((l) => l.time).find(Boolean) ?? '9:00 AM';
  const m = /(\d{1,2})(?::\d{2})?\s*(AM|PM)?/i.exec(t);
  if (!m) return 9;
  let h = Number(m[1]) % 12;
  if ((m[2] ?? 'AM').toUpperCase() === 'PM') h += 12;
  return h;
}

const hourLabel = (h: number) => `${h % 12 === 0 ? 12 : h % 12} ${h < 12 ? 'AM' : 'PM'}`;

async function fetchForecast(draft: TeamContent): Promise<{ weather: TeamContent['weather']; note: string }> {
  const { lat, lon } = draft.venue;
  if (lat === undefined || lon === undefined) throw new Error('Set the venue latitude and longitude first.');
  const next = upcomingMatch(draft.schedule, new Date());
  if (!next) throw new Error('No upcoming match on the schedule to forecast.');
  const url =
    `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}` +
    `&hourly=temperature_2m,precipitation_probability,weather_code` +
    `&daily=temperature_2m_max,temperature_2m_min&temperature_unit=fahrenheit` +
    `&timezone=America%2FNew_York&start_date=${next.iso}&end_date=${next.iso}`;
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Open-Meteo answered ${response.status}.`);
  const data = await response.json();
  const temps: number[] = data?.hourly?.temperature_2m ?? [];
  const pops: number[] = data?.hourly?.precipitation_probability ?? [];
  const codes: number[] = data?.hourly?.weather_code ?? [];
  if (temps.length < 24) throw new Error('Open-Meteo has no hourly forecast for that day yet (it covers 16 days).');
  const first = firstServeHour(draft.lineup);
  const at = (h: number) => Math.min(23, Math.max(0, h));
  const hours: WeatherHour[] = [];
  for (let h = first - 1; h < first + 5; h++) {
    const i = at(h);
    hours.push({
      time: hourLabel(i),
      temp: Math.round(temps[i]),
      pop: Math.round(pops[i] ?? 0),
      storm: [95, 96, 99].includes(codes[i]),
      ...(i === first ? { note: 'First serve' } : {}),
    });
  }
  const maxPop = (from: number, to: number) => Math.max(0, ...pops.slice(at(from), at(to) + 1).map((p) => p ?? 0));
  const chance = maxPop(first, first + 3);
  const laterChance = maxPop(13, 18);
  const storm = codes.slice(at(first), at(first + 4)).some((c) => [95, 96, 99].includes(c));
  const now = new Date();
  const pulledOn = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const icon = storm ? '⛈️' : chance >= 60 ? '🌧️' : chance >= 25 ? '🌥️' : '☀️';
  const weather = {
    ...draft.weather,
    pulledOn,
    pulledAt: now.toISOString(),
    icon,
    chance,
    laterChance,
    high: Math.round(data?.daily?.temperature_2m_max?.[0] ?? Math.max(...temps)),
    low: Math.round(data?.daily?.temperature_2m_min?.[0] ?? Math.min(...temps)),
    firstServe: Math.round(temps[at(first)]),
    noon: Math.round(temps[12]),
    hours,
  };
  return {
    weather,
    note: `Forecast for ${next.label} pulled ${pulledOn}: ${chance}% rain around first serve, ${weather.firstServe}°F at ${hourLabel(first)}, high ${weather.high}°F. The prose is yours to update.`,
  };
}

/* ── The page ──────────────────────────────────────────────────────────── */

export default function Admin() {
  usePageTitle("Admin — Chuck's Mojito Shack");
  const [passcode, setPasscode] = useState<string | null>(() => {
    try {
      return window.sessionStorage.getItem(ADMIN_KEY);
    } catch {
      return null;
    }
  });
  const [entry, setEntry] = useState('');
  const [gateError, setGateError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState<Kind>('team');
  const [team, setTeam] = useState<TeamContent | null>(null);
  const [kourt, setKourt] = useState<KourtContent | null>(null);
  const [roster, setRoster] = useState<RosterContent | null>(null);
  const [rosterError, setRosterError] = useState<string | null>(null);
  const [dirty, setDirty] = useState<Record<Kind, boolean>>({ team: false, kourt: false, roster: false });
  const [status, setStatus] = useState<string | null>(null);
  const [summary, setSummary] = useState('');
  const [preview, setPreview] = useState(false);
  const [versions, setVersions] = useState<Version[] | null>(null);

  const anyDirty = dirty.team || dirty.kourt || dirty.roster;

  // Closing the tab with unsaved edits loses them — the browser asks first.
  useEffect(() => {
    if (!anyDirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [anyDirty]);

  const load = useCallback(async () => {
    const [t, k] = await Promise.all([fetchContent('team'), fetchContent('kourt')]);
    setTeam(t.content);
    setKourt(k.content);
    if (passcode) {
      const r = await fetchRoster(passcode);
      setRoster(r.content);
      setRosterError(
        r.content
          ? null
          : r.status === 503
            ? 'The server has no roster key yet (APP_SESSION_SECRET or ROSTER_KEY).'
            : r.status === 401
              ? 'Passcode rejected — unlock again.'
              : 'Could not load the roster.',
      );
    }
    setDirty({ team: false, kourt: false, roster: false });
    setStatus(
      t.content
        ? `Loaded ${t.source === 'live' ? 'the published content' : 'the shipped seed (API unreachable)'}.`
        : 'Could not load the content.',
    );
  }, [passcode]);

  useEffect(() => {
    void load();
  }, [load]);

  // Fireball regulars can invite a fill-in from the app, which republishes the
  // roster. Pick that up — quietly when the Roster tab has no edits, with a
  // warning when it does, so a Save here can't silently undo the invite.
  const rosterDirty = useRef(false);
  rosterDirty.current = dirty.roster;
  useEffect(() => {
    if (!passcode) return;
    let socket: WebSocket | null = null;
    let retry: number | undefined;
    let closed = false;
    const connect = () => {
      const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
      socket = new WebSocket(`${scheme}://${window.location.host}/ws/scores`);
      socket.onmessage = async (event) => {
        let frame: { type?: string; data?: { kind?: string } } | null = null;
        try {
          frame = JSON.parse(String(event.data));
        } catch {
          return;
        }
        if (frame?.type !== 'content' || frame.data?.kind !== 'roster') return;
        if (rosterDirty.current) {
          setStatus('The roster was just changed elsewhere (a fill-in from the app?). Reload from server before you Save, or you will undo it.');
          return;
        }
        const r = await fetchRoster(passcode);
        if (r.content && !rosterDirty.current) setRoster(r.content);
      };
      socket.onclose = () => {
        if (!closed) retry = window.setTimeout(connect, 5000);
      };
    };
    connect();
    return () => {
      closed = true;
      window.clearTimeout(retry);
      socket?.close();
    };
  }, [passcode]);

  const unlock = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setGateError(null);
    try {
      const response = await fetch('/api/content/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passcode: entry }),
      });
      if (response.status === 401) setGateError('That is not the passcode.');
      else if (response.status === 503) setGateError('No admin passcode has been configured on the server yet.');
      else if (response.status === 429) setGateError('Too many tries — wait ten minutes.');
      else if (!response.ok) setGateError(`The server answered ${response.status}.`);
      else {
        window.sessionStorage.setItem(ADMIN_KEY, entry);
        setPasscode(entry);
      }
    } catch {
      setGateError('Could not reach the server.');
    }
    setBusy(false);
  };

  const lock = () => {
    window.sessionStorage.removeItem(ADMIN_KEY);
    setPasscode(null);
  };

  const publish = async () => {
    if (!passcode) return;
    const content = kind === 'team' ? team : kind === 'kourt' ? kourt : roster;
    if (!content) return;
    setBusy(true);
    setStatus('Publishing…');
    try {
      const response = await fetch(`/api/content/${kind}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passcode, content, summary, by: 'admin' }),
      });
      const body = await response.json().catch(() => ({}));
      if (response.status === 401) {
        lock();
        setStatus('The passcode was rejected — unlock again.');
      } else if (!response.ok) {
        setStatus(`Publish failed: ${body.reason ?? response.status}.`);
      } else {
        if (kind === 'team') setTeam(sanitizeTeamContent(body.content));
        else if (kind === 'kourt') setKourt(sanitizeKourtContent(body.content));
        else setRoster(sanitizeRosterContent(body.content));
        setDirty((d) => ({ ...d, [kind]: false }));
        setSummary('');
        setVersions(null);
        setStatus(
          kind === 'roster'
            ? `Roster saved at ${new Date().toLocaleTimeString()}. Ruling emails, texts and app sign-ins use it from now on.`
            : `Saved and published at ${new Date().toLocaleTimeString()}. Every open ${kind === 'team' ? 'Team' : 'Kourt'} page and the app now show it.`,
        );
      }
    } catch {
      setStatus('Could not reach the server.');
    }
    setBusy(false);
  };

  const loadHistory = async () => {
    if (!passcode) return;
    const response = await fetch(`/api/content/${kind}/history`, { headers: { Authorization: `Bearer ${passcode}` } });
    const body = await response.json().catch(() => ({}));
    setVersions(Array.isArray(body.versions) ? body.versions : []);
  };

  const restore = async (name: string) => {
    if (!passcode) return;
    const response = await fetch(`/api/content/${kind}/history?version=${encodeURIComponent(name)}`, {
      headers: { Authorization: `Bearer ${passcode}` },
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok || !body.content) {
      setStatus('Could not load that version.');
      return;
    }
    if (kind === 'team') setTeam(sanitizeTeamContent(body.content));
    else if (kind === 'kourt') setKourt(sanitizeKourtContent(body.content));
    else setRoster(sanitizeRosterContent(body.content));
    setDirty((d) => ({ ...d, [kind]: true }));
    setStatus(`Loaded the version from ${new Date(body.content.updatedAt).toLocaleString()} into the editor — publish to make it live.`);
  };

  const updateTeam = (next: TeamContent) => {
    setTeam(next);
    setDirty((d) => ({ ...d, team: true }));
  };
  const updateKourt = (next: KourtContent) => {
    setKourt(next);
    setDirty((d) => ({ ...d, kourt: true }));
  };
  const updateRoster = (next: RosterContent) => {
    setRoster(next);
    setDirty((d) => ({ ...d, roster: true }));
  };

  const importRoster = async () => {
    if (!passcode || !roster) return;
    setBusy(true);
    setStatus('Asking the box for the roster…');
    try {
      const response = await fetch('/api/content/roster/import', { headers: { Authorization: `Bearer ${passcode}` } });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.reason ?? `HTTP ${response.status}`);
      const incoming = sanitizeRosterContent({ players: body.players })?.players ?? [];
      const byName = new Map(roster.players.map((p) => [p.name.toLowerCase(), p]));
      let added = 0;
      let filled = 0;
      for (const p of incoming) {
        const mine = byName.get(p.name.toLowerCase());
        if (!mine) {
          byName.set(p.name.toLowerCase(), p);
          added += 1;
        } else {
          // Fill blanks only — what you typed here wins over what ALTA has.
          const next = { ...mine };
          if (!next.email && p.email) {
            next.email = p.email;
            filled += 1;
          }
          if (!next.phone && p.phone) {
            next.phone = p.phone;
            filled += 1;
          }
          if (!next.role && p.role) next.role = p.role;
          byName.set(p.name.toLowerCase(), next);
        }
      }
      updateRoster({ ...roster, players: [...byName.values()].sort((a, b) => a.name.localeCompare(b.name)) });
      setStatus(
        `Imported from ALTA: ${added} player${added === 1 ? '' : 's'} added, ${filled} blank${filled === 1 ? '' : 's'} filled. Nothing you typed was overwritten. Save & Publish to keep it.`,
      );
    } catch (e) {
      setStatus(`Import failed: ${e instanceof Error ? e.message : 'unknown error'}.`);
    }
    setBusy(false);
  };

  const override = useMemo(() => ({ team: team ?? undefined, kourt: kourt ?? undefined }), [team, kourt]);

  if (!passcode) {
    return (
      <div className="admin-page page-content">
        <div className="admin-gate">
          <span className="section-tag">Chuck's Mojito Shack</span>
          <h1>Admin</h1>
          <p>The Team and Kourt pages — and the app — read what is published here. Passcode, please.</p>
          <form onSubmit={unlock} className="admin-gate-form">
            <input
              type="password"
              autoComplete="current-password"
              placeholder="Admin passcode"
              aria-label="Admin passcode"
              value={entry}
              onChange={(e) => setEntry(e.target.value)}
              required
            />
            <button type="submit" disabled={busy}>
              {busy ? 'Checking…' : 'Unlock'}
            </button>
          </form>
          {gateError && (
            <p className="admin-error" role="alert">
              {gateError}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page page-content">
      <div className="admin-head">
        <div>
          <span className="section-tag">Chuck's Mojito Shack</span>
          <h1>Admin</h1>
          <p>Edit, preview, publish. Publishing updates the website and the app at once.</p>
        </div>
        <button type="button" className="admin-lock" onClick={lock}>
          Lock
        </button>
      </div>

      <Reports passcode={passcode} />

      <div className="admin-tabs" role="tablist">
        {(['team', 'kourt', 'roster'] as Kind[]).map((k) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={kind === k}
            className={`admin-tab${kind === k ? ' admin-tab-active' : ''}`}
            onClick={() => {
              setKind(k);
              setVersions(null);
              setPreview(false);
            }}
          >
            {KIND_LABEL[k]}
            {dirty[k] && <span className="admin-dirty-dot" title="Unpublished changes" />}
          </button>
        ))}
      </div>

      <div className="admin-publish">
        <input
          type="text"
          placeholder="What changed? (goes in the history log)"
          aria-label="Change summary"
          value={summary}
          maxLength={200}
          onChange={(e) => setSummary(e.target.value)}
        />
        <button
          type="button"
          className="admin-primary"
          onClick={publish}
          disabled={busy || !dirty[kind]}
          title={dirty[kind] ? 'Save these edits and make them live' : 'No unsaved changes'}
        >
          {busy ? 'Saving…' : dirty[kind] ? '💾 Save & Publish' : 'Saved'}
        </button>
        {kind !== 'roster' && (
          <button type="button" onClick={() => setPreview((p) => !p)}>
            {preview ? 'Back to editor' : 'Preview'}
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            if (!anyDirty || window.confirm('Discard your unsaved changes and reload what is published?')) void load();
          }}
          disabled={busy}
        >
          {anyDirty ? 'Discard changes' : 'Reload from server'}
        </button>
        <button type="button" onClick={loadHistory}>
          History
        </button>
      </div>
      {dirty[kind] ? (
        <p className="admin-unsaved" role="alert">
          ⚠️ <strong>Unsaved changes on the {KIND_LABEL[kind]}.</strong> Nothing is live yet — hit{' '}
          <strong>Save &amp; Publish</strong> when you are done.
        </p>
      ) : (
        <p className="admin-saved" role="status">
          ✅ Everything on this tab is saved and live.
        </p>
      )}
      {status && (
        <p className="admin-status" role="status">
          {status}
        </p>
      )}
      {(() => {
        const current = kind === 'team' ? team : kind === 'kourt' ? kourt : roster;
        return current && current.updatedAt ? (
          <p className="admin-meta">
            Live copy last published {new Date(current.updatedAt).toLocaleString()} by {current.updatedBy}
            {dirty[kind] && <strong> · unpublished edits in this editor</strong>}
          </p>
        ) : null;
      })()}

      {versions && (
        <div className="admin-history">
          <h3>History</h3>
          {versions.length === 0 ? (
            <p>No publishes recorded yet.</p>
          ) : (
            <ul>
              {versions.map((v) => (
                <li key={v.name}>
                  <span>{new Date(v.at).toLocaleString()}</span>
                  <span>{v.by}</span>
                  <span>{v.summary || '—'}</span>
                  <button type="button" onClick={() => restore(v.name)}>
                    Load into editor
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {preview ? (
        <div className="admin-preview">
          <p className="admin-preview-note">
            Preview of the {kind === 'team' ? 'Team' : 'Kourt'} page with the draft above. Scores, messages and
            the docket are live; only the managed copy is the draft.
          </p>
          <ContentOverride.Provider value={override}>{kind === 'team' ? <Team /> : <Kourt />}</ContentOverride.Provider>
        </div>
      ) : kind === 'team' ? (
        team ? (
          <TeamEditor draft={team} onChange={updateTeam} setStatus={setStatus} />
        ) : (
          <p>Loading…</p>
        )
      ) : kind === 'kourt' ? (
        kourt ? (
          <KourtEditor draft={kourt} onChange={updateKourt} />
        ) : (
          <p>Loading…</p>
        )
      ) : roster ? (
        <RosterEditor draft={roster} onChange={updateRoster} onImport={importRoster} busy={busy} />
      ) : (
        <p className="admin-error">{rosterError ?? 'Loading the roster…'}</p>
      )}
      {!preview && (
        <div className="admin-publish admin-publish-foot">
          <span className="admin-hint">{dirty[kind] ? 'You have unsaved changes above.' : 'No unsaved changes.'}</span>
          <button type="button" className="admin-primary" onClick={publish} disabled={busy || !dirty[kind]}>
            {busy ? 'Saving…' : dirty[kind] ? '💾 Save & Publish' : 'Saved'}
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Team editor ───────────────────────────────────────────────────────── */

function TeamEditor({
  draft,
  onChange,
  setStatus,
}: {
  draft: TeamContent;
  onChange: (next: TeamContent) => void;
  setStatus: (s: string) => void;
}) {
  const [json, setJson] = useState('');
  /** Bumped when the board is wiped, so the live editor below reloads. */
  const [liveKey, setLiveKey] = useState(0);
  const [archiveWeek, setArchiveWeek] = useState<number>(() => upcomingMatch(draft.schedule, new Date())?.week ?? 0);
  const set = <K extends keyof TeamContent>(key: K, value: TeamContent[K]) => onChange({ ...draft, [key]: value });

  const setLine = (i: number, patch: Partial<Line>) =>
    set(
      'lineup',
      draft.lineup.map((l, j) => (j === i ? { ...l, ...patch } : l)),
    );
  const setPlayer = (i: number, p: 0 | 1, name: string) => {
    const current = draft.lineup[i].players ?? [{ name: '' }, { name: '' }];
    const players: [Player, Player] = [{ ...current[0] }, { ...current[1] }];
    players[p] = { ...players[p], name };
    setLine(i, { players: players[0].name || players[1].name ? players : null });
  };
  const setMatch = (i: number, patch: Partial<Match>) =>
    set(
      'schedule',
      draft.schedule.map((m, j) => (j === i ? { ...m, ...patch } : m)),
    );
  const setResult = (i: number, patch: Partial<MatchResult> | null) => {
    const m = draft.schedule[i];
    if (patch === null) {
      const { result: _drop, ...rest } = m;
      void _drop;
      set(
        'schedule',
        draft.schedule.map((x, j) => (j === i ? (rest as Match) : x)),
      );
      return;
    }
    setMatch(i, { result: { us: 0, them: 0, lines: [], ...m.result, ...patch } });
  };
  const setHome = (patch: Partial<TeamContent['homeCard']>) => set('homeCard', { ...draft.homeCard, ...patch });
  const setNotices = (which: 'page' | 'nextUp', notices: Notice[]) =>
    which === 'page' ? set('notices', notices) : set('nextUp', { ...draft.nextUp, notices });

  /** The live scoreboard, as a result for the schedule: this week's lineup
   *  names, each line's sets and who took it, and the match tally. Throws
   *  when the blob cannot be read. The note is kept from any existing result. */
  const resultFromScoreboard = async (at: number): Promise<MatchResult> => {
    const { scores } = await loadRuntimeSettings();
    if (!scores) throw new Error('Scoreboard storage is not configured.');
    const response = await fetch(`${scores.blobUrl}?${scores.sas}`, { cache: 'no-store' });
    if (!response.ok) throw new Error(`Could not read the scoreboard (HTTP ${response.status}).`);
    const data = sanitizeScores(await response.json(), draft);
    if (!data.updatedAt) throw new Error(`The scoreboard has nothing posted for ${draft.seasonKey}.`);
    const tally = data.lines.map(lineResult);
    return {
      us: tally.filter((t) => t.winner === 'us').length,
      them: tally.filter((t) => t.winner === 'them').length,
      ...(draft.schedule[at]?.result?.note ? { note: draft.schedule[at].result!.note } : {}),
      lines: draft.lineup.map((line, i) => {
        const played = (data.lines[i] ?? []).filter((s) => s.us !== null && s.them !== null);
        return {
          id: line.id,
          players: (line.players ?? []).map((p) => p.name).join(' / '),
          sets: played.map((s) => `${s.us}-${s.them}`).join(' ') || '0-0',
          won: tally[i]?.winner === 'us',
        };
      }),
    };
  };

  /** Blank the players in the draft AND wipe the live scoreboard blob — the
   *  board is shared state, so that part happens right now, not on publish.
   *  Open pages hear about it on the relay; the rest catch up on their poll. */
  const clearLineupAndScores = async () => {
    if (
      !window.confirm(
        'This blanks every player in the lineup (saved when you publish) AND wipes the live scoreboard for everyone right now. ' +
          'If this week\u2019s result is not in the schedule yet, copy it there first. Continue?',
      )
    )
      return;
    set('lineup', draft.lineup.map((l) => ({ ...l, players: null })));
    try {
      await writeLiveScores(emptyScores(draft));
      setLiveKey((k) => k + 1);
      setStatus('Players cleared in the draft and the live scoreboard wiped. Type the new lineup, then Save & Publish.');
    } catch {
      setStatus('Players cleared in the draft, but the live scoreboard could not be wiped — try again.');
    }
  };

  /** Fill one match's result from the live board, nothing else changes. */
  const copyScoreboard = async (at: number) => {
    try {
      const result = await resultFromScoreboard(at);
      set(
        'schedule',
        draft.schedule.map((x, j) => (j === at ? { ...x, result } : x)),
      );
      setStatus(`Copied the live scoreboard into Week ${draft.schedule[at].week}: ${result.us}–${result.them}. Check the lines, add a note, then publish.`);
    } catch (e) {
      setStatus(e instanceof Error ? e.message : 'Could not read the scoreboard.');
    }
  };

  const archive = async () => {
    const at = draft.schedule.findIndex((m) => m.week === archiveWeek);
    if (at < 0) {
      setStatus('Pick a week to archive into.');
      return;
    }
    try {
      const result = await resultFromScoreboard(at);
      const m = /^(.*wk)(\d+)$/.exec(draft.seasonKey);
      const nextKey = m ? `${m[1]}${Number(m[2]) + 1}` : `${draft.seasonKey}-next`;
      onChange({
        ...draft,
        seasonKey: nextKey,
        schedule: draft.schedule.map((x, j) => (j === at ? { ...x, result } : x)),
      });
      setStatus(
        `Archived the scoreboard into Week ${archiveWeek} as ${result.us}–${result.them} and moved the season key to ${nextKey}. ` +
          'Now set next week’s lineup, venue and copy, then publish — the board goes blank for everyone at that moment.',
      );
    } catch (e) {
      setStatus(e instanceof Error ? e.message : 'Could not read the scoreboard.');
    }
  };

  const forecast = async () => {
    try {
      const { weather, note } = await fetchForecast(draft);
      set('weather', weather);
      setStatus(note);
    } catch (e) {
      setStatus(e instanceof Error ? e.message : 'Forecast fetch failed.');
    }
  };

  const mapsFromAddress = () => {
    const q = encodeURIComponent(`${draft.venue.name.split(' — ')[0]}, ${draft.venue.address}`);
    const a = encodeURIComponent(draft.venue.address);
    set('venue', {
      ...draft.venue,
      googleMaps: `https://www.google.com/maps/dir/?api=1&destination=${q}`,
      appleMaps: `https://maps.apple.com/?daddr=${a}&dirflg=d`,
    });
  };

  return (
    <div className="admin-editor">
      <Section title="This week: lineup and court times" open>
        <div className="admin-grid admin-grid-lineup">
          <span className="admin-col-head">Line</span>
          <span className="admin-col-head">Time</span>
          <span className="admin-col-head">Player 1</span>
          <span className="admin-col-head">Player 2</span>
          <span />
          {draft.lineup.map((l, i) => (
            <div key={i} className="admin-row">
              <input type="text" value={l.id} maxLength={4} onChange={(e) => setLine(i, { id: e.target.value })} />
              <input type="text" value={l.time ?? ''} placeholder="9:00 AM" onChange={(e) => setLine(i, { time: e.target.value })} />
              <input type="text" value={l.players?.[0].name ?? ''} placeholder="(not posted)" onChange={(e) => setPlayer(i, 0, e.target.value)} />
              <input type="text" value={l.players?.[1].name ?? ''} onChange={(e) => setPlayer(i, 1, e.target.value)} />
              <button type="button" className="admin-x" onClick={() => set('lineup', draft.lineup.filter((_, j) => j !== i))} aria-label="Remove line">
                ✕
              </button>
            </div>
          ))}
        </div>
        <div className="admin-actions">
          <button type="button" onClick={() => set('lineup', [...draft.lineup, { id: `${draft.lineup.length + 1}D`, time: draft.lineup[0]?.time, players: null }])}>
            + Add a line
          </button>
          <button
            type="button"
            title="Blank every player (kept until you publish) and wipe the live scoreboard (immediately)"
            onClick={clearLineupAndScores}
          >
            Clear lineup &amp; scores
          </button>
          <Field label="Sets per line">
            <Num value={draft.setsPerLine} min={1} max={3} onChange={(v) => set('setsPerLine', v)} />
          </Field>
          <Field label="Season key" hint="The scoreboard blob's stamp. Change it and the board goes blank for the new week.">
            <Text value={draft.seasonKey} onChange={(v) => set('seasonKey', v)} />
          </Field>
        </div>
        <LiveScoreboard key={liveKey} draft={draft} setStatus={setStatus} />
        <Field label="Date line on the Next Up card" hint="The first-serve time is appended from the lineup.">
          <Text value={draft.nextUp.dateLine} onChange={(v) => set('nextUp', { ...draft.nextUp, dateLine: v })} />
        </Field>
        <Field label="Lineup note" hint="Mini-markup: **bold**, [text](/path).">
          <Text rows={3} value={draft.nextUp.lineupNote} onChange={(v) => set('nextUp', { ...draft.nextUp, lineupNote: v })} />
        </Field>
        <NoticeList title="Notes on the Next Up card (mojitos and the like)" notices={draft.nextUp.notices} onChange={(n) => setNotices('nextUp', n)} defaultStyle="mojito" />
      </Section>

      <Section title="Venue">
        <Field label="Name">
          <Text value={draft.venue.name} onChange={(v) => set('venue', { ...draft.venue, name: v })} />
        </Field>
        <Field label="Address">
          <Text value={draft.venue.address} onChange={(v) => set('venue', { ...draft.venue, address: v })} />
        </Field>
        <Field label="Courts line">
          <Text value={draft.venue.courts} onChange={(v) => set('venue', { ...draft.venue, courts: v })} />
        </Field>
        <div className="admin-inline">
          <Field label="Latitude">
            <input type="number" step="0.0001" value={draft.venue.lat ?? ''} onChange={(e) => set('venue', { ...draft.venue, lat: e.target.value === '' ? undefined : Number(e.target.value) })} />
          </Field>
          <Field label="Longitude">
            <input type="number" step="0.0001" value={draft.venue.lon ?? ''} onChange={(e) => set('venue', { ...draft.venue, lon: e.target.value === '' ? undefined : Number(e.target.value) })} />
          </Field>
        </div>
        <Field label="Google Maps link">
          <Text value={draft.venue.googleMaps} onChange={(v) => set('venue', { ...draft.venue, googleMaps: v })} />
        </Field>
        <Field label="Apple Maps link">
          <Text value={draft.venue.appleMaps} onChange={(v) => set('venue', { ...draft.venue, appleMaps: v })} />
        </Field>
        <button type="button" onClick={mapsFromAddress}>
          Build both map links from the address
        </button>
      </Section>

      <Section title="Weather">
        <div className="admin-actions">
          <button type="button" className="admin-primary" onClick={forecast}>
            Fetch the forecast for the next match (Open-Meteo)
          </button>
          <span className="admin-hint">
            Uses the venue's latitude/longitude and the next match date. Fills the numbers and hours; the prose below is
            yours to update.
          </span>
        </div>
        <div className="admin-inline">
          <Field label="Icon">
            <Text value={draft.weather.icon} onChange={(v) => set('weather', { ...draft.weather, icon: v })} />
          </Field>
          <Field label="Pulled on (shown)">
            <Text value={draft.weather.pulledOn} onChange={(v) => set('weather', { ...draft.weather, pulledOn: v })} />
          </Field>
          <Field label="Place">
            <Text value={draft.weather.place} onChange={(v) => set('weather', { ...draft.weather, place: v })} />
          </Field>
        </div>
        <Field label="Kicker">
          <Text value={draft.weather.kicker} onChange={(v) => set('weather', { ...draft.weather, kicker: v })} />
        </Field>
        <Field label="Headline">
          <Text value={draft.weather.headline} onChange={(v) => set('weather', { ...draft.weather, headline: v })} />
        </Field>
        <Field label="Lede" hint="Placeholders: {chance} {laterChance} {firstServe} {noon} {high} {low}. Mini-markup allowed.">
          <Text rows={4} value={draft.weather.lede} onChange={(v) => set('weather', { ...draft.weather, lede: v })} />
        </Field>
        <Field label="Footer" hint="Placeholders: {pulledOn} {place}.">
          <Text rows={2} value={draft.weather.foot} onChange={(v) => set('weather', { ...draft.weather, foot: v })} />
        </Field>
        <div className="admin-inline">
          {(['chance', 'laterChance', 'firstServe', 'noon', 'high', 'low'] as const).map((k) => (
            <Field key={k} label={k}>
              <Num value={draft.weather[k]} onChange={(v) => set('weather', { ...draft.weather, [k]: v })} />
            </Field>
          ))}
        </div>
        <div className="admin-grid admin-grid-hours">
          <span className="admin-col-head">Hour</span>
          <span className="admin-col-head">°F</span>
          <span className="admin-col-head">Rain %</span>
          <span className="admin-col-head">Storm</span>
          <span className="admin-col-head">Note</span>
          <span />
          {draft.weather.hours.map((h, i) => {
            const setHour = (patch: Partial<WeatherHour>) =>
              set('weather', { ...draft.weather, hours: draft.weather.hours.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
            return (
              <div key={i} className="admin-row">
                <input type="text" value={h.time} onChange={(e) => setHour({ time: e.target.value })} />
                <Num value={h.temp} onChange={(v) => setHour({ temp: v })} />
                <Num value={h.pop} min={0} max={100} onChange={(v) => setHour({ pop: v })} />
                <input type="checkbox" checked={h.storm} onChange={(e) => setHour({ storm: e.target.checked })} />
                <input type="text" value={h.note ?? ''} onChange={(e) => setHour({ note: e.target.value || undefined })} />
                <button type="button" className="admin-x" aria-label="Remove hour" onClick={() => set('weather', { ...draft.weather, hours: draft.weather.hours.filter((_, j) => j !== i) })}>
                  ✕
                </button>
              </div>
            );
          })}
        </div>
        <button type="button" onClick={() => set('weather', { ...draft.weather, hours: [...draft.weather.hours, { time: '', temp: 70, pop: 0, storm: false }] })}>
          + Add an hour
        </button>
      </Section>

      <Section title="Schedule and results">
        <div className="admin-archive">
          <strong>Weekly rollover.</strong> After a match: pick its week, archive the scoreboard into it (the season
          key moves on and the board goes blank for everyone when you publish), then set the new lineup above.
          <div className="admin-actions">
            <select value={archiveWeek} onChange={(e) => setArchiveWeek(Number(e.target.value))} aria-label="Week to archive into">
              <option value={0}>Week…</option>
              {draft.schedule
                .filter((m) => !m.bye)
                .map((m) => (
                  <option key={m.week} value={m.week}>
                    Week {m.week} · {m.label} · {m.opponent}
                    {m.result ? ' (has a result)' : ''}
                  </option>
                ))}
            </select>
            <button type="button" className="admin-primary" onClick={archive} disabled={!archiveWeek}>
              Archive the live scoreboard into this week
            </button>
          </div>
        </div>
        {draft.schedule.map((m, i) => (
          <div key={i} className={`admin-match${m.result ? ' admin-match-played' : ''}`}>
            <div className="admin-inline">
              <Field label="Week">
                <Num value={m.week} min={1} max={30} onChange={(v) => setMatch(i, { week: v })} />
              </Field>
              <Field label="Date (ISO)">
                <input type="date" value={m.iso} onChange={(e) => setMatch(i, { iso: e.target.value })} />
              </Field>
              <Field label="Label">
                <Text value={m.label} onChange={(v) => setMatch(i, { label: v })} />
              </Field>
              <Field label="Rain makeups through" hint="Optional. Keeps this week Next Up until the last makeup day.">
                <input type="date" value={m.through ?? ''} onChange={(e) => setMatch(i, { through: e.target.value || undefined })} />
              </Field>
              <label className="admin-check">
                <input type="checkbox" checked={Boolean(m.bye)} onChange={(e) => setMatch(i, { bye: e.target.checked || undefined })} /> Bye
              </label>
              <label className="admin-check">
                <input type="checkbox" checked={Boolean(m.away)} onChange={(e) => setMatch(i, { away: e.target.checked })} /> Away
              </label>
              <button type="button" className="admin-x" aria-label="Remove match" onClick={() => set('schedule', draft.schedule.filter((_, j) => j !== i))}>
                ✕
              </button>
            </div>
            {!m.bye && (
              <div className="admin-inline">
                <Field label="Opponent">
                  <Text value={m.opponent ?? ''} onChange={(v) => setMatch(i, { opponent: v })} />
                </Field>
                <Field label="Their captain">
                  <Text value={m.opponentCaptain ?? ''} onChange={(v) => setMatch(i, { opponentCaptain: v })} />
                </Field>
              </div>
            )}
            {!m.bye &&
              (m.result ? (
                <div className="admin-result">
                  <div className="admin-inline">
                    <Field label="Lines won (us)">
                      <Num value={m.result.us} min={0} max={20} onChange={(v) => setResult(i, { us: v })} />
                    </Field>
                    <Field label="Lines lost (them)">
                      <Num value={m.result.them} min={0} max={20} onChange={(v) => setResult(i, { them: v })} />
                    </Field>
                    <button type="button" className="admin-primary" onClick={() => copyScoreboard(i)}>
                      Copy from the live scoreboard
                    </button>
                    <button type="button" onClick={() => setResult(i, null)}>
                      Remove result (back to "still to play")
                    </button>
                  </div>
                  <Field label="Note under the result" hint="Plain text. The Kourt summary for the week goes here.">
                    <Text rows={3} value={m.result.note ?? ''} onChange={(v) => setResult(i, { note: v || undefined })} />
                  </Field>
                  <div className="admin-grid admin-grid-lines">
                    <span className="admin-col-head">Line</span>
                    <span className="admin-col-head">Players</span>
                    <span className="admin-col-head">Sets (ours first)</span>
                    <span className="admin-col-head">Won</span>
                    <span />
                    {m.result.lines.map((l, k) => {
                      const setLS = (patch: Partial<typeof l>) =>
                        setResult(i, { lines: m.result!.lines.map((x, q) => (q === k ? { ...x, ...patch } : x)) });
                      return (
                        <div key={k} className="admin-row">
                          <input type="text" value={l.id} onChange={(e) => setLS({ id: e.target.value })} />
                          <input type="text" value={l.players} onChange={(e) => setLS({ players: e.target.value })} />
                          <input type="text" value={l.sets} placeholder="6-3 4-6 10-7" onChange={(e) => setLS({ sets: e.target.value })} />
                          <input type="checkbox" checked={l.won} onChange={(e) => setLS({ won: e.target.checked })} />
                          <button type="button" className="admin-x" aria-label="Remove line" onClick={() => setResult(i, { lines: m.result!.lines.filter((_, q) => q !== k) })}>
                            ✕
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <button type="button" onClick={() => setResult(i, { lines: [...m.result!.lines, { id: `${m.result!.lines.length + 1}D`, players: '', sets: '', won: false }] })}>
                    + Add a line
                  </button>
                </div>
              ) : (
                <div className="admin-actions">
                  <button type="button" className="admin-primary" onClick={() => copyScoreboard(i)}>
                    Copy from the live scoreboard
                  </button>
                  <button type="button" onClick={() => setResult(i, { us: 0, them: 0, lines: [] })}>
                    Enter a result by hand
                  </button>
                </div>
              ))}
          </div>
        ))}
        <button
          type="button"
          onClick={() => {
            const last = draft.schedule[draft.schedule.length - 1];
            const d = last ? new Date(`${last.iso}T12:00:00`) : new Date();
            d.setDate(d.getDate() + 7);
            const iso = d.toISOString().slice(0, 10);
            set('schedule', [
              ...draft.schedule,
              { week: (last?.week ?? 0) + 1, iso, label: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }), away: false },
            ]);
          }}
        >
          + Add a week
        </button>
        <Field label="Schedule footnote">
          <Text rows={2} value={draft.scheduleFoot} onChange={(v) => set('scheduleFoot', v)} />
        </Field>
      </Section>

      <Section title="Home page card">
        <p className="admin-hint">
          The tennis card on the Home page. The chips are automatic: <strong>Next</strong> from the schedule and one per
          played week from its result, so archiving a week keeps them current. Rewrite the headline and paragraph each
          week, then Save &amp; Publish — the site updates without a deploy.
        </p>
        <div className="admin-inline">
          <label className="admin-check">
            <input type="checkbox" checked={draft.homeCard.show} onChange={(e) => setHome({ show: e.target.checked })} /> Show the card
          </label>
          <label className="admin-check">
            <input type="checkbox" checked={draft.homeCard.resultChips} onChange={(e) => setHome({ resultChips: e.target.checked })} /> Next and result
            chips
          </label>
          <Field label="Emoji">
            <Text value={draft.homeCard.emoji} onChange={(v) => setHome({ emoji: v })} />
          </Field>
        </div>
        <Field label="Headline" hint="Mini-markup: **bold**, [text](/path).">
          <Text value={draft.homeCard.headline} onChange={(v) => setHome({ headline: v })} />
        </Field>
        <Field label="Paragraph" hint="Mini-markup: **bold**, [text](/path).">
          <Text rows={4} value={draft.homeCard.body} onChange={(v) => setHome({ body: v })} />
        </Field>
        <Field label="Extra chip (optional)" hint="Shown first and dark, e.g. “🍹 Mojitos today · after 3D”. Leave empty for none.">
          <Text value={draft.homeCard.chip} onChange={(v) => setHome({ chip: v })} />
        </Field>
      </Section>

      <Section title="Copy: header, banner and notices">
        <Field label="Section tag">
          <Text value={draft.header.tag} onChange={(v) => set('header', { ...draft.header, tag: v })} />
        </Field>
        <Field label="Header paragraph" hint="Mini-markup: **bold**, [text](/path).">
          <Text rows={4} value={draft.header.paragraph} onChange={(v) => set('header', { ...draft.header, paragraph: v })} />
        </Field>
        <Field label="Kourt tease (blank to hide)">
          <Text rows={2} value={draft.header.kourtTease} onChange={(v) => set('header', { ...draft.header, kourtTease: v })} />
        </Field>
        <Field label="Next Up sentence, within a week of the match">
          <Text rows={2} value={draft.nextUp.bannerSoon} onChange={(v) => set('nextUp', { ...draft.nextUp, bannerSoon: v })} />
        </Field>
        <Field label="Next Up sentence, further out">
          <Text rows={2} value={draft.nextUp.bannerFar} onChange={(v) => set('nextUp', { ...draft.nextUp, bannerFar: v })} />
        </Field>
        <Field label="Season complete sentence">
          <Text rows={2} value={draft.nextUp.seasonDone} onChange={(v) => set('nextUp', { ...draft.nextUp, seasonDone: v })} />
        </Field>
        <NoticeList title="Page notices (cookout duty and the like)" notices={draft.notices} onChange={(n) => setNotices('page', n)} defaultStyle="cookout" />
        <Field label="Hall of Shame charges, one per line">
          <Text rows={5} value={draft.charges.join('\n')} onChange={(v) => set('charges', lines(v))} />
        </Field>
      </Section>

      <Section title="Season facts">
        <div className="admin-inline">
          {(['name', 'club', 'rosterId', 'division', 'coordinator', 'captain', 'coCaptain'] as const).map((k) => (
            <Field key={k} label={k}>
              <Text value={draft.season[k]} onChange={(v) => set('season', { ...draft.season, [k]: v })} />
            </Field>
          ))}
        </div>
      </Section>

      <Section title="Advanced: raw JSON">
        <p className="admin-hint">Everything above, as the JSON the site and the app read. Paste and apply to replace the whole draft.</p>
        <textarea rows={12} value={json || JSON.stringify(draft, null, 2)} onChange={(e) => setJson(e.target.value)} spellCheck={false} />
        <div className="admin-actions">
          <button
            type="button"
            onClick={() => {
              try {
                const parsed = sanitizeTeamContent(JSON.parse(json));
                if (!parsed) throw new Error('rejected');
                onChange(parsed);
                setJson('');
                setStatus('Applied the JSON to the draft.');
              } catch {
                setStatus('That JSON is not valid team content.');
              }
            }}
            disabled={!json}
          >
            Apply JSON
          </button>
          <button type="button" onClick={() => setJson('')} disabled={!json}>
            Discard edits
          </button>
        </div>
      </Section>
    </div>
  );
}

function NoticeList({
  title,
  notices,
  onChange,
  defaultStyle,
}: {
  title: string;
  notices: Notice[];
  onChange: (n: Notice[]) => void;
  defaultStyle: string;
}) {
  return (
    <div className="admin-notices">
      <span className="admin-label">{title}</span>
      {notices.map((n, i) => {
        const patch = (p: Partial<Notice>) => onChange(notices.map((x, j) => (j === i ? { ...x, ...p } : x)));
        return (
          <div key={i} className="admin-notice">
            <div className="admin-inline">
              <Field label="Icon">
                <Text value={n.icon} onChange={(v) => patch({ icon: v })} />
              </Field>
              <Field label="Style">
                <Text value={n.style} onChange={(v) => patch({ style: v })} />
              </Field>
              <button type="button" className="admin-x" aria-label="Remove notice" onClick={() => onChange(notices.filter((_, j) => j !== i))}>
                ✕
              </button>
            </div>
            <Text rows={3} value={n.text} onChange={(v) => patch({ text: v })} />
          </div>
        );
      })}
      <button type="button" onClick={() => onChange([...notices, { id: `n${Date.now()}`, style: defaultStyle, icon: '📣', text: '' }])}>
        + Add a notice
      </button>
    </div>
  );
}

/* ── Kourt editor ──────────────────────────────────────────────────────── */

function KourtEditor({ draft, onChange }: { draft: KourtContent; onChange: (next: KourtContent) => void }) {
  const set = <K extends keyof KourtContent>(key: K, value: KourtContent[K]) => onChange({ ...draft, [key]: value });
  const setJudge = (patch: Partial<KourtContent['judge']>) => set('judge', { ...draft.judge, ...patch });
  const setGuideline = (i: number, patch: Partial<Guideline>) =>
    set(
      'guidelines',
      draft.guidelines.map((g, j) => (j === i ? { ...g, ...patch } : g)),
    );
  return (
    <div className="admin-editor">
      <Section title="The Bench" open>
        <Field label="Judge's name" hint="Also used in ruling emails, the summons PDF and texts.">
          <Text value={draft.judge.name} onChange={(v) => setJudge({ name: v })} />
        </Field>
        <div className="admin-inline">
          <Field label="Roster name">
            <Text value={draft.judge.rosterName} onChange={(v) => setJudge({ rosterName: v })} />
          </Field>
          <Field label="After the name">
            <Text value={draft.judge.presiding} onChange={(v) => setJudge({ presiding: v })} />
          </Field>
          <Field label="Paw tag">
            <Text value={draft.judge.paw} onChange={(v) => setJudge({ paw: v })} />
          </Field>
        </div>
        <Field label="Portrait caption">
          <Text value={draft.judge.tagline} onChange={(v) => setJudge({ tagline: v })} />
        </Field>
        <Field label="Portrait path" hint="Under /images/. Blank hides the portrait.">
          <Text value={draft.judge.portrait} onChange={(v) => setJudge({ portrait: v })} />
        </Field>
        <Field label="Portrait alt text">
          <Text value={draft.judge.portraitAlt} onChange={(v) => setJudge({ portraitAlt: v })} />
        </Field>
        <Field label="Kicker">
          <Text value={draft.kicker} onChange={(v) => set('kicker', v)} />
        </Field>
        <Field label="Lede" hint="Mini-markup: **bold**.">
          <Text rows={3} value={draft.lede} onChange={(v) => set('lede', v)} />
        </Field>
        <Field label="Rules of the Kourt, one per line">
          <Text rows={8} value={draft.rules.join('\n')} onChange={(v) => set('rules', lines(v))} />
        </Field>
      </Section>
      <Section title="Sentencing guidelines" open>
        <Field label="Most a complaint may ask for ($)">
          <Num value={draft.complaintMax} min={1} max={500} onChange={(v) => set('complaintMax', v)} />
        </Field>
        <div className="admin-grid admin-grid-guidelines">
          <span className="admin-col-head">Chip</span>
          <span className="admin-col-head">Kind</span>
          <span className="admin-col-head">$</span>
          <span className="admin-col-head">Offense</span>
          <span />
          {draft.guidelines.map((g, i) => (
            <div key={i} className="admin-row">
              <input type="text" value={g.label} onChange={(e) => setGuideline(i, { label: e.target.value })} />
              <select value={g.kind} onChange={(e) => setGuideline(i, { kind: e.target.value === 'bonus' ? 'bonus' : 'fine' })}>
                <option value="fine">fine</option>
                <option value="bonus">bonus</option>
              </select>
              <Num value={g.amount} min={0} max={500} onChange={(v) => setGuideline(i, { amount: v })} />
              <input type="text" value={g.offense} onChange={(e) => setGuideline(i, { offense: e.target.value })} />
              <button type="button" className="admin-x" aria-label="Remove guideline" onClick={() => set('guidelines', draft.guidelines.filter((_, j) => j !== i))}>
                ✕
              </button>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => set('guidelines', [...draft.guidelines, { label: '', kind: 'fine', amount: 2, offense: '' }])}>
          + Add a guideline
        </button>
      </Section>
      <Section title="Opinion openers">
        <Field label="One per line">
          <Text rows={8} value={draft.openers.join('\n')} onChange={(v) => set('openers', lines(v))} />
        </Field>
      </Section>
    </div>
  );
}

/* ── Roster editor ─────────────────────────────────────────────────────── */

function RosterEditor({
  draft,
  onChange,
  onImport,
  busy,
}: {
  draft: RosterContent;
  onChange: (next: RosterContent) => void;
  onImport: () => void;
  busy: boolean;
}) {
  const setPlayer = (i: number, patch: Partial<RosterPlayer>) =>
    onChange({ ...draft, players: draft.players.map((p, j) => (j === i ? { ...p, ...patch } : p)) });
  const remove = (i: number) => {
    const p = draft.players[i];
    if (window.confirm(`Remove ${p.name || 'this player'} from the roster directory?`)) {
      onChange({ ...draft, players: draft.players.filter((_, j) => j !== i) });
    }
  };
  const add = () =>
    onChange({ ...draft, players: [...draft.players, { name: '', email: '', phone: '', role: '', notes: '', fireball: false, altEmails: [], fillInFor: '', fillInBy: '' }] });
  const missing = draft.players.filter((p) => !p.email).length;
  const fireballs = draft.players.filter((p) => p.fireball).length;
  const practice = nextPractice();
  const fillIns = draft.players.filter((p) => !p.fireball && p.fillInFor === practice.iso);
  return (
    <div className="admin-editor">
      <Section title="Team directory" open>
        <p className="admin-hint">
          Names, roles, emails and mobile numbers. This is what the Kourt uses to email and text a defendant, and what
          the app checks an Apple ID against at sign-in — it outranks what the box scraped from ALTA. Stored
          encrypted; only this page and the server can read it. {draft.players.length} player
          {draft.players.length === 1 ? '' : 's'}, {missing} without an email.
        </p>
        <p className="admin-hint">
          🔥 <strong>Fireball</strong> marks the Thursday practice group. A marked player gets a Fireball tab in the app
          to say IN or OUT each week, and a reminder at 4 PM from Monday until he answers. {fireballs} marked.
        </p>
        <p className="admin-hint">
          🎾 <strong>Fill-in</strong>: one practice only. <strong>Fill in {practice.label}</strong> gives a non-Fireball
          player the Fireball tab for that practice; at 6:30 PM that Thursday his access ends by itself. Save &amp; Publish
          to send it.{' '}
          {fillIns.length
            ? `This week: ${fillIns.map((p) => (p.fillInBy && p.fillInBy !== 'admin' ? `${p.name} (invited by ${p.fillInBy})` : p.name)).join(', ')}.`
            : 'Nobody filling in this week.'}{' '}
          Fireball regulars can also invite one from the app.
        </p>
        <div className="admin-actions">
          <button type="button" className="admin-primary" onClick={onImport} disabled={busy}>
            {busy ? 'Working…' : 'Import from ALTA (fills blanks, adds missing players)'}
          </button>
          <button type="button" onClick={add}>
            + Add a player
          </button>
        </div>
        <div className="admin-grid admin-grid-roster">
          {draft.players.map((p, i) => (
            <div key={i} className={`admin-row${p.fireball ? ' admin-row-fireball' : ''}`}>
              <label className="admin-fireball" title="Fireball: the Thursday practice group">
                <input type="checkbox" checked={p.fireball} onChange={(e) => setPlayer(i, { fireball: e.target.checked })} />
                <span aria-hidden="true">🔥</span>
                <span className="visually-hidden">Fireball</span>
              </label>
              <div className="admin-name-cell">
                <input type="text" placeholder="Name (as ALTA shows it)" aria-label="Name" value={p.name} onChange={(e) => setPlayer(i, { name: e.target.value })} />
                {!p.fireball &&
                  (p.fillInFor === practice.iso ? (
                    <button
                      type="button"
                      className="admin-fillin admin-fillin-on"
                      title={`Filling in for this practice${p.fillInBy && p.fillInBy !== 'admin' ? ` (invited by ${p.fillInBy})` : ''} — click to cancel`}
                      onClick={() => setPlayer(i, { fillInFor: '', fillInBy: '' })}
                    >
                      🎾 Fill-in {practice.short} ✕
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="admin-fillin"
                      title={`Give ${p.name || 'him'} the Fireball tab for ${practice.label} only`}
                      onClick={() => setPlayer(i, { fillInFor: practice.iso, fillInBy: 'admin' })}
                    >
                      Fill in {practice.short}
                    </button>
                  ))}
              </div>
              <input type="email" placeholder="email" aria-label="Email" value={p.email} onChange={(e) => setPlayer(i, { email: e.target.value.trim().toLowerCase() })} />
              <input
                type="tel"
                placeholder="mobile"
                aria-label="Mobile"
                value={p.phone}
                onChange={(e) => setPlayer(i, { phone: e.target.value })}
                onBlur={(e) => setPlayer(i, { phone: e.target.value.trim() ? normalizePhone(e.target.value) || e.target.value : '' })}
                className={p.phone && !normalizePhone(p.phone) ? 'admin-invalid' : undefined}
                title={p.phone && !normalizePhone(p.phone) ? 'Not a US mobile number — it will be dropped on save' : undefined}
              />
              <select aria-label="Role" value={p.role} onChange={(e) => setPlayer(i, { role: e.target.value as RosterPlayer['role'] })}>
                <option value="">Player</option>
                <option value="Captain">Captain</option>
                <option value="Co-Captain">Co-Captain</option>
              </select>
              <input
                type="text"
                placeholder="other sign-in emails"
                aria-label="Other sign-in emails"
                title="Other addresses he signs in with, e.g. his Gmail for the Android app. Comma-separated. Notices still go to the main email."
                value={p.altEmails.join(', ')}
                onChange={(e) => setPlayer(i, { altEmails: e.target.value.split(/[\s,;]+/).map((v) => v.trim().toLowerCase()) })}
                onBlur={() => setPlayer(i, { altEmails: p.altEmails.filter((v, k, all) => v.includes('@') && all.indexOf(v) === k) })}
              />
              <input type="text" placeholder="notes" aria-label="Notes" value={p.notes} onChange={(e) => setPlayer(i, { notes: e.target.value })} />
              <button type="button" className="admin-x" aria-label={`Remove ${p.name}`} onClick={() => remove(i)}>
                ✕
              </button>
            </div>
          ))}
        </div>
        {draft.players.length === 0 && <p className="admin-hint">Empty. Import from ALTA to start, or add players by hand.</p>}
      </Section>
    </div>
  );
}

/** The next Thursday practice a fill-in can still join: today if it's Thursday
 *  before 6:30 PM, otherwise the coming Thursday. Local time (Chuck is Eastern). */
function nextPractice(now = new Date()): { iso: string; label: string; short: string } {
  const day = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let ahead = (4 - day.getDay() + 7) % 7;
  if (ahead === 0 && now.getHours() * 60 + now.getMinutes() >= 18 * 60 + 30) ahead = 7;
  day.setDate(day.getDate() + ahead);
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    iso: `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`,
    label: day.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }),
    short: day.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
  };
}

/* ── Reports: what players flagged in the app ──────────────────────────── */

interface Report {
  rowKey: string;
  kind: 'message' | 'case';
  key: string;
  author: string;
  excerpt: string;
  reason: string;
  by: string;
  at: string;
  hidden: boolean;
}

/** Reports from the last 30 days. Hide removes the post from the app and the
 *  site for everyone (an add-only `hidden` row; there is no un-hide). */
function Reports({ passcode }: { passcode: string }) {
  const [reports, setReports] = useState<Report[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const response = await fetch('/api/app/moderation', { headers: { Authorization: `Bearer ${passcode}` } });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.reason ?? `HTTP ${response.status}`);
      setReports((body.reports as Report[]).sort((a, b) => b.at.localeCompare(a.at)));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load reports.');
    }
  }, [passcode]);

  useEffect(() => {
    void load();
  }, [load]);

  const hide = async (r: Report) => {
    if (!window.confirm(`Hide this ${r.kind === 'message' ? 'message' : 'complaint'} by ${r.author} from the app and the site? This cannot be undone.`)) return;
    setBusy(r.key);
    try {
      const response = await fetch('/api/app/moderation', {
        method: 'POST',
        headers: { Authorization: `Bearer ${passcode}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: r.key, kind: r.kind }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Hide failed.');
    }
    setBusy(null);
  };

  const open = (reports ?? []).filter((r) => !r.hidden);
  return (
    <details className="admin-reports" open={open.length > 0}>
      <summary>
        🚩 Reports {reports ? `— ${open.length ? `${open.length} open` : 'nothing open'}` : ''}
      </summary>
      <p className="admin-hint">
        Players can report a message or a Kourt complaint from the app; each report is also emailed to the moderator.
        <strong> Hide</strong> removes it from the app and the site for everyone. Respond within a day.
      </p>
      {error && <p className="admin-error">{error}</p>}
      {reports && reports.length === 0 && <p className="admin-hint">No reports in the last 30 days.</p>}
      <ul>
        {(reports ?? []).map((r) => (
          <li key={r.rowKey} className={r.hidden ? 'admin-report-done' : ''}>
            <div>
              <strong>{r.kind === 'message' ? 'Message' : 'Complaint'} by {r.author}</strong>{' '}
              <span className="admin-hint">
                reported by {r.by}, {new Date(r.at).toLocaleString()}
              </span>
              <blockquote>{r.excerpt}</blockquote>
              {r.reason && <div className="admin-hint">Reason: {r.reason}</div>}
            </div>
            {r.hidden ? (
              <span className="admin-hint">Hidden</span>
            ) : (
              <button type="button" className="admin-primary" disabled={busy === r.key} onClick={() => void hide(r)}>
                {busy === r.key ? 'Hiding…' : 'Hide'}
              </button>
            )}
          </li>
        ))}
      </ul>
    </details>
  );
}
